import type {
  LoginRequest,
  LoginResponse,
  User,
  CategoriesResponse,
  ArticlesResponse,
  ArticleResponse,
  SearchResponse,
  CommentsResponse,
  CreateCommentRequest,
  UpdateCommentRequest,
  ArticleVersionsResponse,
  ArticleVersion,
  APIResponse,
} from "@/types/api";

const API_URL = import.meta.env.VITE_KB_API_URL;

if (!API_URL) {
  throw new Error("VITE_KB_API_URL environment variable is not set");
}

class APIError extends Error {
  constructor(public status: number, message: string) {
    super(message);
    this.name = "APIError";
  }
}

// Helper function to handle API responses
async function handleResponse<T>(response: Response): Promise<T> {
  console.log("🔄 handleResponse", {
    status: response.status,
    statusText: response.statusText,
    ok: response.ok,
    url: response.url
  });

  if (!response.ok) {
    let errorMessage = `HTTP ${response.status}`;
    let errorData = null;

    try {
      errorData = await response.json();
      errorMessage = errorData.error || errorMessage;
      console.error("❌ API Error Response:", errorData);
    } catch (parseError) {
      console.error("❌ Failed to parse error response:", parseError);
    }

    if (response.status === 401) {
      console.warn("🔐 Unauthorized - clearing token and redirecting");
      localStorage.removeItem("auth_token");
      window.location.href = "/app/login";
      throw new APIError(401, "Unauthorized");
    }

    console.error("❌ API Error:", {
      status: response.status,
      message: errorMessage,
      errorData
    });

    throw new APIError(response.status, errorMessage);
  }

  return response.json();
}

// Helper function to make authenticated requests
async function fetchWithAuth(
  endpoint: string,
  options: RequestInit = {}
): Promise<Response> {
  const token = localStorage.getItem("auth_token");

  console.log("🔐 fetchWithAuth", {
    endpoint,
    method: options.method || 'GET',
    hasToken: !!token,
    tokenPreview: token ? token.substring(0, 20) + '...' : null
  });

  const headers = new Headers(options.headers);
  if (token) {
    headers.set("Authorization", `Bearer ${token}`);
  }
  headers.set("Content-Type", "application/json");

  const response = await fetch(`${API_URL}${endpoint}`, {
    ...options,
    headers,
  });

  console.log("🔐 fetchWithAuth response", {
    status: response.status,
    statusText: response.statusText,
    url: response.url
  });

  return response;
}

// Auth API
export const auth = {
  async login(credentials: LoginRequest): Promise<LoginResponse> {
    console.log("🔍 API Call: POST /api/auth/login", credentials);
    const response = await fetch(`${API_URL}/api/auth/login`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(credentials),
    });

    const result = await handleResponse<LoginResponse>(response);
    console.log("📦 API Response: POST /api/auth/login", result);
    return result;
  },

  async me(): Promise<User> {
    console.log("🔍 API Call: GET /api/auth/me");
    const response = await fetchWithAuth("/api/auth/me");
    const result = await handleResponse<{ user: User }>(response);
    console.log("📦 API Response: GET /api/auth/me", result);
    return result.user;
  },

  logout() {
    localStorage.removeItem("auth_token");
    window.location.href = "/app/login";
  },
};

// Categories API
export const categories = {
  async list(): Promise<CategoriesResponse> {
    console.log("🔍 API Call: GET /api/categories");
    const response = await fetchWithAuth("/api/categories");
    const result = await handleResponse<CategoriesResponse>(response);
    console.log("📦 API Response: GET /api/categories", result);
    return result;
  },
};

// Articles API
export const articles = {
  async list(params: { category?: string } = {}): Promise<ArticlesResponse> {
    const searchParams = new URLSearchParams();
    if (params.category) {
      searchParams.set("category", params.category);
    }

    const endpoint = `/api/articles${searchParams.toString() ? `?${searchParams}` : ""}`;
    console.log("🔍 API Call: GET", endpoint);

    const response = await fetchWithAuth(endpoint);
    const result = await handleResponse<ArticlesResponse>(response);
    console.log("📦 API Response: GET", endpoint, result);
    return result;
  },

  async getBySlug(slug: string): Promise<ArticleResponse> {
    console.log("🔍 API Call: GET /api/articles/" + slug);
    const response = await fetchWithAuth(`/api/articles/${encodeURIComponent(slug)}`);
    const result = await handleResponse<ArticleResponse>(response);
    console.log("📦 API Response: GET /api/articles/" + slug, result);
    return result;
  },

  async create(data: any): Promise<ArticleResponse> {
    console.log("🔍 API Call: POST /api/articles", data);
    const response = await fetchWithAuth("/api/articles", {
      method: "POST",
      body: JSON.stringify(data),
    });
    const result = await handleResponse<ArticleResponse>(response);
    console.log("📦 API Response: POST /api/articles", result);
    return result;
  },

  async update(slug: string, data: any): Promise<ArticleResponse> {
    console.log("🔍 API Call: PUT /api/articles/" + slug, data);
    const response = await fetchWithAuth(`/api/articles/${encodeURIComponent(slug)}`, {
      method: "PUT",
      body: JSON.stringify(data),
    });
    const result = await handleResponse<ArticleResponse>(response);
    console.log("📦 API Response: PUT /api/articles/" + slug, result);
    return result;
  },

  async delete(slug: string): Promise<{ success: boolean }> {
    console.log("🔍 API Call: DELETE /api/articles/" + slug);
    const response = await fetchWithAuth(`/api/articles/${encodeURIComponent(slug)}`, {
      method: "DELETE",
    });
    const result = await handleResponse<{ success: boolean }>(response);
    console.log("📦 API Response: DELETE /api/articles/" + slug, result);
    return result;
  },
};

// Comments API
export const comments = {
  // Get comments for an article (supports nested tree structure)
  async list(articleSlug: string): Promise<CommentsResponse> {
    console.log("🔍 API Call: GET /api/articles/" + articleSlug + "/comments");
    const response = await fetchWithAuth(`/api/articles/${articleSlug}/comments`);
    const result = await handleResponse<CommentsResponse>(response);
    console.log("📦 API Response: GET /api/articles/" + articleSlug + "/comments", result);
    return result;
  },

  // Create a root comment on an article
  async create(articleSlug: string, data: CreateCommentRequest): Promise<{ comment: any }> {
    console.log("🔍 Starting comment creation for slug:", articleSlug);
    console.log("🔍 Input data:", JSON.stringify(data, null, 2));

    // Send simple payload - Worker will handle all the complexity
    const payload = {
      body: data.body
    };

    // Only add parent_id if it's explicitly provided and valid
    if (data.parent_id !== undefined && data.parent_id !== null && data.parent_id > 0) {
      payload.parent_id = data.parent_id;
    }

    console.log("🔍 Final payload being sent:", JSON.stringify(payload, null, 2));

    const response = await fetchWithAuth(`/api/articles/${articleSlug}/comments`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    const result = await handleResponse<{ comment: any }>(response);
    console.log("📦 API Response: POST /api/articles/" + articleSlug + "/comments", result);
    return result;
  },

  // Create a reply to an existing comment
  async reply(commentId: number, data: CreateCommentRequest): Promise<{ comment: any }> {
    console.log("🔍 API Call: POST /api/comments/" + commentId + "/reply", data);
    const response = await fetchWithAuth(`/api/comments/${commentId}/reply`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });
    const result = await handleResponse<{ comment: any }>(response);
    console.log("📦 API Response: POST /api/comments/" + commentId + "/reply", result);
    return result;
  },

  // Update an existing comment (edit body or resolve status)
  async update(commentId: number, data: UpdateCommentRequest): Promise<{ comment: any }> {
    console.log("🔍 API Call: PATCH /api/comments/" + commentId, data);
    const response = await fetchWithAuth(`/api/comments/${commentId}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });
    const result = await handleResponse<{ comment: any }>(response);
    console.log("📦 API Response: PATCH /api/comments/" + commentId, result);
    return result;
  },

  // Delete a comment (future enhancement)
  async delete(commentId: number): Promise<void> {
    console.log("🔍 API Call: DELETE /api/comments/" + commentId);
    const response = await fetchWithAuth(`/api/comments/${commentId}`, {
      method: 'DELETE',
    });
    await handleResponse(response);
    console.log("📦 API Response: DELETE /api/comments/" + commentId + " - success");
  },

  // Add emoji reaction to a comment
  async addReaction(commentId: number, emoji: string): Promise<{ reactions: Record<string, number> }> {
    console.log("🔍 API Call: POST /api/comments/" + commentId + "/react", { emoji });
    const response = await fetchWithAuth(`/api/comments/${commentId}/react`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ emoji }),
    });
    const result = await handleResponse<{ reactions: Record<string, number> }>(response);
    console.log("📦 API Response: POST /api/comments/" + commentId + "/react", result);
    return result;
  },

  // Remove emoji reaction from a comment
  async removeReaction(commentId: number, emoji: string): Promise<{ reactions: Record<string, number> }> {
    console.log("🔍 API Call: DELETE /api/comments/" + commentId + "/react?emoji=" + emoji);
    const response = await fetchWithAuth(`/api/comments/${commentId}/react?emoji=${encodeURIComponent(emoji)}`, {
      method: 'DELETE',
    });
    const result = await handleResponse<{ reactions: Record<string, number> }>(response);
    console.log("📦 API Response: DELETE /api/comments/" + commentId + "/react", result);
    return result;
  },
};

// Search API
export interface SearchFilters {
  category?: string;
  author?: string;
  dateRange?: 'today' | 'week' | 'month' | 'year';
  hasAttachments?: boolean;
  contentType?: 'all' | 'title' | 'content';
  sortBy?: 'relevance' | 'updated' | 'created';
}

// Client-side filtering helper until backend supports it
export function applyClientSideFilters(articles: any[], filters: SearchFilters, categories: any[] = []): any[] {
  let filtered = [...articles];

  // Filter by category
  if (filters.category) {
    const targetCategory = categories.find(c => c.name === filters.category);
    if (targetCategory) {
      filtered = filtered.filter(article =>
        article.category_id === targetCategory.id
      );
    }
  }

  // Filter by date range
  if (filters.dateRange) {
    const now = new Date();
    const cutoffDate = new Date();

    switch (filters.dateRange) {
      case 'today':
        cutoffDate.setHours(0, 0, 0, 0);
        break;
      case 'week':
        cutoffDate.setDate(now.getDate() - 7);
        break;
      case 'month':
        cutoffDate.setMonth(now.getMonth() - 1);
        break;
      case 'year':
        cutoffDate.setFullYear(now.getFullYear() - 1);
        break;
    }

    filtered = filtered.filter(article =>
      new Date(article.updated_at) >= cutoffDate
    );
  }

  // Sort results
  if (filters.sortBy) {
    switch (filters.sortBy) {
      case 'updated':
        filtered.sort((a, b) =>
          new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()
        );
        break;
      case 'created':
        filtered.sort((a, b) =>
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        );
        break;
      case 'relevance':
      default:
        // Keep original order for relevance
        break;
    }
  }

  return filtered;
}

export const search = {
  async query(q: string, filters?: SearchFilters): Promise<SearchResponse> {
    // For now, use the basic search endpoint until backend supports filters
    const endpoint = `/api/search?q=${encodeURIComponent(q)}`;
    console.log("🔍 API Call: GET", endpoint, { filters });

    const response = await fetchWithAuth(endpoint);
    const result = await handleResponse<SearchResponse>(response);
    console.log("📦 API Response: GET", endpoint, result);

    // Note: Client-side filtering will be applied at the hook level
    // where we have access to categories data
    return result;
  },

  async suggestions(q: string): Promise<{ suggestions: string[] }> {
    if (!q.trim()) return { suggestions: [] };

    // For now, return some basic suggestions based on common search patterns
    // TODO: Replace with backend endpoint when available
    const commonSuggestions = [
      'CRM setup',
      'brand guidelines',
      'lead management',
      'onboarding process',
      'sales procedures',
      'marketing materials',
      'client communication',
      'property listings'
    ];

    const suggestions = commonSuggestions
      .filter(suggestion =>
        suggestion.toLowerCase().includes(q.toLowerCase()) ||
        q.toLowerCase().includes(suggestion.toLowerCase())
      )
      .slice(0, 3);

    console.log("📦 Search suggestions (client-side):", { query: q, suggestions });
    return { suggestions };
  },
};

// Version History API
export const versions = {
  // Get all versions for an article
  async list(articleSlug: string): Promise<ArticleVersionsResponse> {
    const endpoint = `/api/articles/${articleSlug}/versions`;
    console.log("🔍 API Call: GET", endpoint);

    const response = await fetchWithAuth(endpoint);
    const result = await handleResponse<ArticleVersionsResponse>(response);
    console.log("📦 API Response: GET", endpoint, result);
    return result;
  },

  // Get a specific version
  async get(articleSlug: string, versionId: number): Promise<{ version: ArticleVersion }> {
    const endpoint = `/api/articles/${articleSlug}/versions/${versionId}`;
    console.log("🔍 API Call: GET", endpoint);

    const response = await fetchWithAuth(endpoint);
    const result = await handleResponse<{ version: ArticleVersion }>(response);
    console.log("📦 API Response: GET", endpoint, result);
    return result;
  },

  // Revert to a specific version
  async revert(articleSlug: string, versionId: number, changeSummary?: string): Promise<ArticleResponse> {
    const endpoint = `/api/articles/${articleSlug}/revert/${versionId}`;
    console.log("🔍 API Call: POST", endpoint);

    const body = changeSummary ? { change_summary: changeSummary } : {};

    const response = await fetchWithAuth(endpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(body),
    });
    const result = await handleResponse<ArticleResponse>(response);
    console.log("📦 API Response: POST", endpoint, result);
    return result;
  },

  // Compare two versions (future enhancement)
  async compare(articleSlug: string, fromVersion: number, toVersion: number): Promise<{ diff: any }> {
    const endpoint = `/api/articles/${articleSlug}/versions/compare?from=${fromVersion}&to=${toVersion}`;
    console.log("🔍 API Call: GET", endpoint);

    const response = await fetchWithAuth(endpoint);
    const result = await handleResponse<{ diff: any }>(response);
    console.log("📦 API Response: GET", endpoint, result);
    return result;
  },
};

// Users API (for mentions)
export const users = {
  async search(query: string): Promise<{ users: any[] }> {
    console.log("🔍 API Call: GET /api/users?search=" + query);
    const response = await fetchWithAuth(`/api/users?search=${encodeURIComponent(query)}`);
    const result = await handleResponse<{ users: any[] }>(response);
    console.log("📦 API Response: GET /api/users?search=" + query, result);
    return result;
  },

  async getAll(): Promise<{ users: any[] }> {
    console.log("🔍 API Call: GET /api/users");
    const response = await fetchWithAuth('/api/users');
    const result = await handleResponse<{ users: any[] }>(response);
    console.log("📦 API Response: GET /api/users", result);
    return result;
  },

  async getById(id: string): Promise<{ user: any }> {
    console.log("🔍 API Call: GET /api/users/" + id);
    const response = await fetchWithAuth(`/api/users/${encodeURIComponent(id)}`);
    const result = await handleResponse<{ user: any }>(response);
    console.log("📦 API Response: GET /api/users/" + id, result);
    return result;
  },

  async getByEmail(email: string): Promise<{ user: any }> {
    console.log("🔍 API Call: GET /api/users?email=" + email);
    const response = await fetchWithAuth(`/api/users?email=${encodeURIComponent(email)}`);
    const result = await handleResponse<{ user: any }>(response);
    console.log("📦 API Response: GET /api/users?email=" + email, result);
    return result;
  },
};

// Re-export for convenience
export const kbApi = {
  auth,
  categories,
  articles,
  comments,
  search,
  versions,
  users,
};